// For jwt-decode v4, we need to use the named import
import { jwtDecode } from 'jwt-decode';

/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token to check
 * @returns {boolean} - True if token is expired, false otherwise
 */
export const isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000; // in seconds
    return decoded.exp < currentTime;
  } catch (error) {
    return true; // Treat invalid tokens as expired
  }
};

/**
 * Get token expiry date
 * @param {string} token - JWT token
 * @returns {Date|null} - Token expiry date or null if invalid
 */
export const getTokenExpiry = (token) => {
  try {
    const decoded = jwtDecode(token);
    return new Date(decoded.exp * 1000);
  } catch (error) {
    return null;
  }
};

/**
 * Check if token will expire within a given time
 * @param {string} token - JWT token
 * @param {number} thresholdSeconds - Time threshold in seconds
 * @returns {boolean} - True if token expires within threshold
 */
export const isTokenExpiringSoon = (token, thresholdSeconds = 300) => { // 5 minutes default
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = decoded.exp - currentTime;
    return timeUntilExpiry <= thresholdSeconds;
  } catch (error) {
    return true; // Treat invalid tokens as expiring soon
  }
};

/**
 * Extract user information from JWT token
 * @param {string} token - JWT token
 * @returns {Object|null} - User information or null if invalid
 */
export const extractUserFromToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    return {
      userId: decoded.sub || decoded.userId,
      email: decoded.email,
      role: decoded.role,
      permissions: decoded.permissions,
      exp: decoded.exp,
      iat: decoded.iat,
    };
  } catch (error) {
    return null;
  }
};

/**
 * Check if token should be refreshed (when keepMeLogin is enabled)
 * @param {string} token - JWT token
 * @param {number} thresholdSeconds - Time threshold in seconds before expiry to refresh
 * @returns {boolean} - True if token should be refreshed
 */
export const shouldRefreshToken = (token, thresholdSeconds = 300) => { // 5 minutes default
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = decoded.exp - currentTime;
    return timeUntilExpiry <= thresholdSeconds;
  } catch (error) {
    return false; // Don't refresh invalid tokens
  }
};

/**
 * Get time until token expiry in seconds
 * @param {string} token - JWT token
 * @returns {number} - Time until expiry in seconds, negative if expired
 */
export const getTimeUntilExpiry = (token) => {
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp - currentTime;
  } catch (error) {
    return -1; // Invalid token
  }
};
